import { useEffect, useRef, useCallback } from 'react'
import { Textarea } from '@/shared/components/ui/textarea'
import { Button } from '@/shared/components/ui/button'
import { Label } from '@/shared/components/ui/label'
import { X } from 'lucide-react'
import { useRedactorStore } from '../store/redactorStore'
import { useRedact } from '../hooks/useRedact'
import { useDraft } from '../hooks/useDraft'
import { ControlPanel } from './ControlPanel'

export function RedactorForm() {
  const { inputText, status, setInputText, reset } = useRedactorStore()
  const { redact, abort } = useRedact()
  useDraft()

  const isStreaming = status === 'streaming'
  const submitRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSubmit = useCallback(() => {
    if (submitRef.current) clearTimeout(submitRef.current)
    submitRef.current = setTimeout(() => void redact(), 300)
  }, [redact])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault()
      handleSubmit()
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      if (isStreaming) {
        abort()
      } else {
        reset()
      }
    }
  }

  useEffect(() => {
    return () => {
      if (submitRef.current) clearTimeout(submitRef.current)
    }
  }, [])

  return (
    <div className="flex flex-col gap-4">
      <ControlPanel />

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="input-text">Your message</Label>
        <Textarea
          id="input-text"
          value={inputText}
          onChange={(e) => setInputText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Paste or type your message here..."
          className="min-h-[160px] resize-y font-mono text-sm"
          disabled={isStreaming}
        />
        <p className="text-xs text-muted-foreground">
          {inputText.length} characters &middot; Cmd+Enter to redact &middot; Esc to {isStreaming ? 'abort' : 'clear'}
        </p>
      </div>

      <div className="flex gap-2">
        {isStreaming ? (
          <Button variant="outline" onClick={abort} className="gap-2">
            <X className="h-4 w-4" />
            Abort
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={!inputText.trim()}
            className="gap-2"
          >
            Redact
          </Button>
        )}
        {!isStreaming && (inputText || status !== 'idle') && (
          <Button variant="ghost" onClick={reset}>
            Clear
          </Button>
        )}
      </div>
    </div>
  )
}
